import { ComponentType, lazy } from 'react';
import { createChunkLoadError } from './staleChunkRecovery';

/**
 * Like React's `lazy`, but for a chunk's *named* export.
 *
 * The usual pattern —
 *   lazy(() => import('./Mod').then((m) => ({ default: m.Named })))
 * — blows up with a generic `Cannot read properties of undefined (reading
 * 'Named')` when a deploy has rotated the chunk and the dynamic import resolves
 * to something without the export. That message is not recognized as a chunk
 * failure, so the stale-chunk auto-reload never kicks in and the user is left on
 * the generic error screen.
 *
 * `lazyNamed` guards the export and, when it is missing, throws a proper
 * `ChunkLoadError` that {@link createChunkLoadError} produces — routing the
 * failure through the existing recovery path instead.
 */
export function lazyNamed<
  T extends Record<string, unknown>,
  K extends keyof T & string,
>(importer: () => Promise<T>, exportName: K) {
  return lazy(async () => {
    const module = await importer();
    const Component = module?.[exportName] as
      | ComponentType<unknown>
      | undefined;
    if (!Component) {
      throw createChunkLoadError(exportName);
    }
    return { default: Component };
  });
}

/**
 * Like React's `lazy`, but guards the chunk's *default* export.
 *
 * Plain `lazy(() => import('./Mod'))` lets React read `module.default`
 * itself. When a deploy has rotated the content-hashed chunk, the dynamic
 * import can resolve to an empty/undefined module, and React's lazy
 * initializer then blows up with a generic `Cannot read properties of
 * undefined (reading 'default')` TypeError. That message is not recognized
 * as a chunk failure, so the stale-chunk auto-reload never kicks in and the
 * user is left on the generic error screen.
 *
 * `lazyDefault` validates the default export and, when it is missing, throws
 * a proper `ChunkLoadError` that {@link createChunkLoadError} produces —
 * routing the failure through the existing recovery path instead.
 *
 * The constraint mirrors React's own `lazy` (`ComponentType<any>`) so this is
 * a true drop-in replacement, including for third-party default exports whose
 * props aren't assignable to `unknown`.
 */
export function lazyDefault<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirror React's own `lazy` so any default-export component is accepted
  T extends ComponentType<any>,
>(importer: () => Promise<{ default: T }>) {
  return lazy(async () => {
    const module = (await importer()) as { default?: T } | undefined;
    const Component = module?.default;
    if (!Component) {
      throw createChunkLoadError('default');
    }
    return { default: Component };
  });
}
